import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'

export interface SentryConfig {
  dsn: string
  environment: string
  release?: string
  tracesSampleRate?: number
  replaysSessionSampleRate?: number
  replaysOnErrorSampleRate?: number
}

/**
 * Initialize Sentry error tracking for the Vue application.
 *
 * @param app - The Vue application instance
 * @param router - The Vue Router instance
 * @param config - Sentry configuration options
 */
export function initSentry(app: App, router: Router, config: SentryConfig): void {
  // Skip initialization if no DSN is provided
  if (!config.dsn) {
    console.warn('Sentry DSN not configured, error tracking disabled')
    return
  }

  Sentry.init({
    app,
    dsn: config.dsn,
    environment: config.environment,
    release: config.release,

    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Set sample rates for performance monitoring
    tracesSampleRate: config.tracesSampleRate ?? 0.1,

    // Set sample rates for Session Replay
    replaysSessionSampleRate: config.replaysSessionSampleRate ?? 0.1,
    replaysOnErrorSampleRate: config.replaysOnErrorSampleRate ?? 1.0,

    // Don't send PII automatically
    sendDefaultPii: false,

    // Filter out known safe errors
    beforeSend(event) {
      // Filter out network errors that are expected (e.g., 401 on token refresh)
      if (event.exception?.values?.[0]?.value?.includes('401')) {
        return null
      }
      return event
    },
  })
}

export interface SentryUser {
  id?: string
  email?: string
  username?: string
}

/**
 * Set the current user context for Sentry error reports.
 *
 * @param user - The user data to set, or null to clear the user context
 */
export function setSentryUser(user: SentryUser | null): void {
  try {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.username,
      })
    } else {
      Sentry.setUser(null)
    }
  } catch (error) {
    console.error('Failed to set Sentry user:', error)
  }
}

/**
 * Add a breadcrumb to the Sentry trail.
 *
 * @param category - The category of the breadcrumb (e.g., 'navigation', 'http', 'user')
 * @param message - A descriptive message
 * @param data - Optional additional data
 * @param level - The severity level
 */
export function addSentryBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>,
  level: 'debug' | 'info' | 'warning' | 'error' = 'info'
): void {
  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level,
  })
}

/**
 * Set a tag that will be indexed and searchable in Sentry.
 *
 * @param key - The tag key
 * @param value - The tag value
 */
export function setSentryTag(key: string, value: string): void {
  Sentry.setTag(key, value)
}

/**
 * Set extra context data that will be included with error reports.
 *
 * @param key - The context key
 * @param value - The context value
 */
export function setSentryContext(key: string, value: Record<string, unknown>): void {
  Sentry.setContext(key, value)
}

/**
 * Capture an exception and send it to Sentry.
 *
 * @param error - The error to capture
 * @param context - Optional additional context
 */
export function captureException(error: unknown, context?: Record<string, unknown>): void {
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value)
      })
      Sentry.captureException(error)
    })
  } else {
    Sentry.captureException(error)
  }
}

/**
 * Capture a message and send it to Sentry.
 *
 * @param message - The message to capture
 * @param level - The severity level
 */
export function captureMessage(
  message: string,
  level: 'debug' | 'info' | 'warning' | 'error' | 'fatal' = 'info'
): void {
  Sentry.captureMessage(message, level)
}

// Re-export Sentry for advanced usage
export { Sentry }
